import { FC, ReactNode, memo, type MouseEvent } from 'react';
import { cn } from '@crisellevaldez/speculo-ui';
import { formatNumber } from '../../utils/format';

const LoadingDots: FC = () => (
  <span className="inline-flex">
    <span className="animate-[loading_1.4s_ease-in-out_infinite] opacity-0">.</span>
    <span className="animate-[loading_1.4s_ease-in-out_0.2s_infinite] opacity-0">.</span>
    <span className="animate-[loading_1.4s_ease-in-out_0.4s_infinite] opacity-0">.</span>
  </span>
);

export interface SubStat {
  label: string;
  value: number | string;
  onClick?: () => void;
}

export interface StatCardProps {
  id: string;
  label: ReactNode;
  description?: ReactNode;
  valueLabel?: ReactNode;
  value: number;
  isLoading: boolean;
  icon?: ReactNode;
  highlighted?: boolean;
  subStats?: SubStat[];
  onClick?: () => void;
  isClickable?: boolean;
}

export const StatCard: FC<StatCardProps> = memo(
  ({
    label,
    description,
    valueLabel,
    value,
    isLoading,
    icon,
    highlighted = false,
    subStats,
    onClick,
    isClickable = false,
  }) => {
    const visibleSubStats = subStats?.filter((s) =>
      typeof s.value === 'string' ? s.value.length > 0 : s.value !== 0
    );

    const onGlow = (e: MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      e.currentTarget.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`);
      e.currentTarget.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`);
    };

    return (
      <div
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onClick={isClickable ? onClick : undefined}
        onKeyDown={isClickable ? (e) => e.key === 'Enter' && onClick?.() : undefined}
        onMouseMove={onGlow}
        className={cn(
          'group mouse-glow rounded-xl p-4 2xl:p-5 3xl:p-6 border border-foreground/30 flex flex-col justify-between min-h-[120px] 2xl:min-h-[140px] h-full transition-all duration-300 shadow-lg hover:shadow-xl hover:translate-y-[-2px]',
          isClickable ? 'cursor-pointer' : 'cursor-default',
          highlighted ? 'stat-card-gradient-highlighted' : 'stat-card-gradient'
        )}
      >
        <div className="flex items-center justify-between mb-2 2xl:mb-3">
          <span className="text-xs md:text-sm text-foreground font-semibold tracking-wide uppercase">
            {label}
          </span>
          {icon && (
            <div
              className={cn(
                'p-1 rounded-full',
                highlighted ? 'stat-icon-highlighted' : 'text-muted-foreground bg-muted'
              )}
            >
              {icon}
            </div>
          )}
        </div>

        <div className="space-y-2 2xl:space-y-3">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl md:text-4xl 3xl:text-[42px] font-bold text-primary transition-all duration-300">
              {isLoading ? <LoadingDots /> : formatNumber(value)}
            </span>
            {valueLabel && (
              <span className="text-sm text-muted-foreground font-medium">{valueLabel}</span>
            )}
            {!isLoading && !isClickable && (
              <span className="text-xs text-muted-foreground opacity-0 group-hover:opacity-60 transition-opacity duration-200">
                {value === 0 && onClick ? 'No activity yet' : 'Counts only'}
              </span>
            )}
          </div>

          {/* Always reserve space for sub-stats row to keep cards equal height */}
          <div className="min-h-[28px] 2xl:min-h-[32px] mt-1 space-y-1">
            {description && (
              <p
                className="text-sm font-normal text-muted-foreground"
                style={{ lineHeight: '18px' }}
              >
                {description}
              </p>
            )}
            {visibleSubStats && visibleSubStats.length > 0 && (
              <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5">
                {visibleSubStats.map((s, index) =>
                  s.onClick ? (
                    <button
                      key={`${s.label}-${index}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        s.onClick?.();
                      }}
                      className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <span className="text-sm font-medium whitespace-nowrap">{s.label}:</span>
                      <span className="text-sm text-primaryfortext font-semibold underline decoration-dotted underline-offset-2">
                        {isLoading
                          ? '...'
                          : typeof s.value === 'number'
                            ? formatNumber(s.value)
                            : s.value}
                      </span>
                    </button>
                  ) : (
                    <div
                      key={`${s.label}-${index}`}
                      className="flex items-center gap-1 text-muted-foreground"
                    >
                      <span className="text-sm font-medium whitespace-nowrap">{s.label}:</span>
                      <span className="text-sm text-primaryfortext font-semibold">
                        {isLoading
                          ? '...'
                          : typeof s.value === 'number'
                            ? formatNumber(s.value)
                            : s.value}
                      </span>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);
